import React, { useEffect } from 'react';
import { StyleSheet, View, ViewProps } from 'react-native';
import Animated, {
  useSharedValue,
  withTiming,
  useAnimatedProps,
  withSpring,
  interpolate,
  useDerivedValue,
} from 'react-native-reanimated';
import Svg, { Circle } from 'react-native-svg';
import { styled, SxProp, useSx } from 'dripsy';

import { useColors } from '@hooks';
import { AnimatedText } from '@components';

interface IContainerStyleProps {
  size: number;
}

const Container = styled(View)((props: IContainerStyleProps) => ({
  justifyContent: 'center',
  alignItems: 'center',
  width: props.size,
  height: props.size - 20,
  overflow: 'hidden',
}));

const ProgressContainer = styled(View)(
  (props: IContainerStyleProps) => ({
    ...StyleSheet.absoluteFillObject,
    width: props.size,
    height: props.size,
    transform: [{ rotate: `${0.77 * Math.PI}rad` }],
  }),
);

const textStyle: SxProp = {
  zIndex: 1,
  pt: '$5',
  pl: '$4',
  textAlign: 'center',
  color: '$heading600',
  variant: 'text.xxxlarge',
  fontWeight: 'semi',
};

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

interface ICircularProgressBarProps extends ViewProps {
  size?: number;
  percent?: number;
  strokeWidth?: number;
}

export function CircularProgressBar({
  size = 213,
  percent = 1,
  strokeWidth = 9,
  ...props
}: ICircularProgressBarProps) {
  const sx = useSx();
  const colors = useColors();
  const progress = useSharedValue(0);
  const radius = (size - strokeWidth) / 2;
  const length = 2 * Math.PI * radius;

  useEffect(() => {
    progress.value = withTiming(percent, { duration: 1000 });
  }, []);

  const animatedProps = useAnimatedProps(() => {
    const offset = interpolate(
      progress.value,
      [0, 1],
      [length, length * 0.3],
    );
    return {
      strokeDashoffset: withSpring(offset),
    };
  });

  const progressText = useDerivedValue(() => {
    return `${Math.floor(progress.value * 100)}%`;
  });

  return (
    <Container size={size} {...props}>
      <ProgressContainer size={size}>
        <Svg width={size} height={size}>
          <Circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={colors.$background40}
            strokeWidth={strokeWidth / 2}
            strokeDasharray={length}
            strokeDashoffset={length * 0.3}
            strokeLinecap="round"
            fill="transparent"
          />
          <AnimatedCircle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={colors.$primary300}
            strokeWidth={strokeWidth}
            strokeDasharray={length}
            animatedProps={animatedProps}
            strokeLinecap="round"
            fill="transparent"
          />
        </Svg>
      </ProgressContainer>

      <AnimatedText style={sx(textStyle)} text={progressText} />
    </Container>
  );
}
